import { round } from "lodash";
import { _60, FORMAT_MONTH_DAY } from "reports/utils/timeUtils";
import { format } from "date-fns";
import {
  SterisChangoversType,
  SterisFormattedDataType,
} from "./sterisChangeoverTypes";

export const BEGINNING = ":beginning";
export const CENTER = ":center";
export const ENDING = ":ending";

export const SHIFT_1 = "Shift 1";
export const SHIFT_2 = "Shift 2";

export const SETUP_DURATION_LIMIT = 30;
export const SETUP_DURATION_EXCEEDED_LIMIT = 220;
export const SETUP_DURATION_TOTAL_LIMIT =
  SETUP_DURATION_LIMIT + SETUP_DURATION_EXCEEDED_LIMIT;

type SterisDowntimeEventType = {
  duration: number;
  date: string;
  order: number;
  ge: number;
};

/**
 * Changes date format from numbers to letters
 * Converts changeover times to minutes and gets total
 *
 * @param {*} data
 * @returns
 */
export const formatData = (
  data: SterisChangoversType[]
): SterisFormattedDataType[] => {
  const formattedData = data.map(element => {
    const date = format(parseInt(element.range.ge, 10), FORMAT_MONTH_DAY);
    const changeoverDuration = element.changeoverDuration * 60;
    const changeoverDurationExceeded = element.changeoverDurationExceeded * 60;
    const changeoverDurationExceededLim =
      changeoverDurationExceeded > SETUP_DURATION_EXCEEDED_LIMIT
        ? SETUP_DURATION_EXCEEDED_LIMIT
        : changeoverDurationExceeded;

    return {
      ...element,
      date,
      changeoverDuration,
      changeoverDurationExceeded,
      changeoverDurationExceededLim,
      total: round(changeoverDuration + changeoverDurationExceeded),
    };
  });

  // Only want to show columns where changeoverDuration is greater than 2 minutes
  return formattedData.filter(
    ({ changeoverDuration }) => changeoverDuration > 2
  );
};

type SterisFormattedWithOrderType = SterisFormattedDataType & {
  order: number;
};

/**
 * Renames dates depending on what the format will be picked up
 * later by the tickFormatter
 *
 * @param {SterisFormattedWithOrderType[]} data
 * @returns
 */
export const getAxisTick = (
  data: (SterisFormattedWithOrderType | SterisDowntimeEventType)[]
): (SterisFormattedWithOrderType | SterisDowntimeEventType)[] => {
  const dates = data.map(datum => datum.date);

  // Get unique dates
  const distinctDates = dates.filter((value, index, thisArray) => {
    return thisArray.indexOf(value) === index;
  });

  distinctDates.forEach(distinctDate => {
    // Get indexes of all dates that are equal to distinctDate
    const dateIndexPositions = dates.reduce(
      (acc, date, i) => (date === distinctDate ? [...acc, i] : acc),
      [] as number[]
    );

    const minIndex = Math.min(...dateIndexPositions);
    const maxIndex = Math.max(...dateIndexPositions);
    const centerIndex = round((maxIndex + minIndex) / 2);

    const hasTrueCenterIndex =
      (maxIndex + minIndex) % 2 === 0 || (maxIndex === 0 && minIndex === 0);

    // Add suffix to first, center and last instances of distinctDate
    // Used for date tick formats
    data[centerIndex].date += hasTrueCenterIndex ? CENTER : ":centerfix";
    data[minIndex].date += BEGINNING;
    data[maxIndex].date += ENDING;
  });
  return data;
};
/**
 * This is an additional method to the one above, just filters to find all unique formatted dates
 *
 * @param {*} data
 * @returns
 */
export const formatNamesTicks = (
  data: (SterisFormattedWithOrderType | SterisDowntimeEventType)[]
): string[] => {
  const dates = data.map(a => a.date);
  return dates.filter(
    date =>
      date.indexOf(CENTER) || date.indexOf(BEGINNING) || date.indexOf(ENDING)
  );
};
